/**
 * Seeded uniform and normal draws for simulations that must be reproducible: the same
 * seed text always produces the same numbers, in any browser, so a reader can share a
 * seed and a teacher can show the same picture to a whole class.
 *
 * The stream is the research-design tools' documented generator (cyrb128 seed hash into
 * sfc32, pinned by their tests), scaled to [0, 1). It is NOT cryptographically secure
 * and is meant for illustrations, never for secrets (use `crypto.getRandomValues`).
 */

import { seededSource } from "@/lib/tool-kit/research-design/random";

export type Rng = () => number;

const TWO_32 = 4_294_967_296;

/** Uniform numbers in [0, 1) from a seed text. */
export function createRng(seed: string): Rng {
  const next = seededSource(seed);
  return () => next() / TWO_32;
}

/**
 * Standard normal draws by the Box–Muller transform. Each pair of uniforms yields two
 * normals, so the second is kept for the next call.
 */
export function createNormalSampler(rng: Rng): () => number {
  let spare: number | null = null;
  return () => {
    if (spare !== null) {
      const value = spare;
      spare = null;
      return value;
    }
    const radius = Math.sqrt(-2 * Math.log(1 - rng()));
    const angle = 2 * Math.PI * rng();
    spare = radius * Math.sin(angle);
    return radius * Math.cos(angle);
  };
}
